import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link } from 'react-router-dom';
import { Phone, Clock, Shield, Users, MessageCircle, Car, CreditCard, FileText, Star, ArrowRight, MapPin, Award, Heart, Zap, Anchor } from 'lucide-react';
import { bailAgentService } from '../../services/bailAgentService';
import EmergencyBailAssistanceForm from '../landing-page/components/EmergencyBailAssistanceForm';

const Homepage = () => {
  const [currentAgent, setCurrentAgent] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch current agent on shift
  useEffect(() => {
    const fetchCurrentAgent = async () => {
      try {
        const { data, error } = await bailAgentService?.getCurrentAgent();
        if (!error && data) {
          setCurrentAgent(data);
        }
      } catch (error) {
        console.error('Failed to fetch current agent:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchCurrentAgent();
  }, []);

  // Constants
  const EMERGENCY_PHONE = '(916) 256-3468';
  const OFFICE_ADDRESS = '901 H Street, Suite 103, Sacramento, CA 95814';

  return (
    <>
      <Helmet>
        <title>Sacramento Bail Bonds 24/7 | Sailors Bail Bonds - Fast, Friendly Service</title>
        <meta 
          name="description" 
          content="Need bail bonds in Sacramento? We're here 24/7 with fast, empathetic service. In bail bonds business since 2009, serving all California counties. Call (916) 256-3468." 
        />
        <meta 
          name="keywords" 
          content="bail bonds Sacramento, 24/7 bail bonds, emergency bail, California bail bonds, Sacramento bail bondsman" 
        />
      </Helmet>
      <div className="min-h-screen bg-[#F8F9FA]">
        {/* Fixed Navigation Header */}
        <header className="fixed top-0 left-0 right-0 bg-white shadow-lg z-50 border-b-4 border-[#007BFF]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between">
              {/* Logo */}
              <Link to="/" className="flex items-center space-x-3">
                <Anchor className="w-10 h-10 text-[#007BFF]" />
                <div>
                  <h1 className="text-2xl font-black text-gray-900" style={{ fontFamily: 'Roboto Slab, serif' }}>
                    Sailors Bail Bonds
                  </h1>
                  <p className="text-sm text-[#007BFF] font-semibold" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                    Since 2009 - Sacramento's Trusted Partner
                  </p>
                </div>
              </Link>

              {/* Navigation Menu */}
              <nav className="hidden lg:flex items-center space-x-8" aria-label="Main navigation">
                <Link 
                  to="/" 
                  className="text-base font-semibold text-gray-900 hover:text-[#007BFF] transition-colors"
                  style={{ fontFamily: 'Open Sans, sans-serif' }}
                >
                  Home
                </Link>
                <Link 
                  to="/about-us" 
                  className="text-base font-semibold text-gray-700 hover:text-[#007BFF] transition-colors"
                  style={{ fontFamily: 'Open Sans, sans-serif' }}
                >
                  About Us
                </Link>
                <Link 
                  to="/payment-plans-page" 
                  className="text-base font-semibold text-gray-700 hover:text-[#007BFF] transition-colors"
                  style={{ fontFamily: 'Open Sans, sans-serif' }}
                >
                  Services
                </Link>
                <Link 
                  to="/inmate-search-widget" 
                  className="text-base font-semibold text-gray-700 hover:text-[#007BFF] transition-colors"
                  style={{ fontFamily: 'Open Sans, sans-serif' }}
                >
                  Inmate Search
                </Link>
                <Link 
                  to="/5-step-bail-guide-with-faq" 
                  className="text-base font-semibold text-gray-700 hover:text-[#007BFF] transition-colors"
                  style={{ fontFamily: 'Open Sans, sans-serif' }}
                >
                  Resources
                </Link>
                <a 
                  href={`tel:${EMERGENCY_PHONE?.replace(/\D/g, '')}`}
                  className="bg-gradient-to-r from-[#28A745] to-green-600 text-white px-6 py-3 rounded-xl font-black text-base hover:from-green-600 hover:to-green-700 transition-all transform hover:scale-105 shadow-lg flex items-center space-x-2"
                  aria-label="Call 24/7 Emergency Help"
                >
                  <Phone className="w-5 h-5" />
                  <span>24/7 Emergency Help</span>
                </a>
              </nav>

              {/* Mobile Menu Button */}
              <button 
                className="lg:hidden bg-[#007BFF] text-white p-3 rounded-lg"
                aria-label="Open mobile menu"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>
        </header>

        {/* Hero Section */}
        <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-[#007BFF] via-blue-600 to-blue-700">
          <div className="max-w-7xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Hero Content */}
              <div className="text-white space-y-6">
                <div className="inline-flex items-center bg-white/20 backdrop-blur-sm px-6 py-3 rounded-full border-2 border-white/30">
                  <Clock className="w-5 h-5 mr-2 animate-pulse" />
                  <span className="font-bold text-base" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                    Available 24/7 - Never Closed
                  </span>
                </div>

                <h2 
                  className="text-5xl lg:text-6xl font-black leading-tight"
                  style={{ fontFamily: 'Roboto Slab, serif' }}
                >
                  Hey, we know this is tough— we're here 24/7 to make bail simple and stress-free
                </h2>

                <p 
                  className="text-xl leading-relaxed text-blue-100"
                  style={{ fontFamily: 'Open Sans, sans-serif' }}
                >
                  Serving Sacramento, Stockton, Modesto, and all California counties with compassionate, understanding support when you need it most.
                </p>

                {/* Current Agent Display */}
                {!loading && currentAgent && (
                  <div className="bg-white/10 backdrop-blur-md border-2 border-white/30 rounded-2xl p-6">
                    <div className="flex items-center space-x-4">
                      {currentAgent?.profile_photo_url ? (
                        <img 
                          src={currentAgent?.profile_photo_url} 
                          alt={`${currentAgent?.full_name} - Licensed Bail Agent`}
                          className="w-20 h-20 rounded-full border-4 border-white shadow-lg object-cover"
                        />
                      ) : (
                        <div className="w-20 h-20 rounded-full border-4 border-white bg-blue-400 flex items-center justify-center">
                          <Users className="w-10 h-10 text-white" />
                        </div>
                      )}
                      <div>
                        <p className="text-sm font-semibold text-blue-200 uppercase tracking-wide">
                          Currently On Shift
                        </p>
                        <p className="text-2xl font-black text-white">
                          {currentAgent?.full_name}
                        </p>
                        <p className="text-base text-blue-100">
                          {currentAgent?.years_experience}+ years experience • License #{currentAgent?.license_number}
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Emergency CTA */}
                <div className="flex flex-col sm:flex-row gap-4 pt-4">
                  <a 
                    href={`tel:${EMERGENCY_PHONE?.replace(/\D/g, '')}`}
                    className="group bg-gradient-to-r from-[#28A745] to-green-600 text-white px-8 py-5 rounded-2xl font-black text-xl hover:from-green-600 hover:to-green-700 transition-all transform hover:scale-105 shadow-2xl flex items-center justify-center space-x-3 border-4 border-green-400"
                    aria-label={`Call emergency line ${EMERGENCY_PHONE}`}
                  >
                    <Phone className="w-7 h-7 group-hover:animate-pulse" />
                    <span>Call Now: {EMERGENCY_PHONE}</span>
                  </a>

                  <a 
                    href="#emergency-form"
                    className="bg-white text-[#007BFF] px-8 py-5 rounded-2xl font-black text-xl hover:bg-gray-100 transition-all transform hover:scale-105 shadow-2xl flex items-center justify-center space-x-3"
                  >
                    <MessageCircle className="w-7 h-7" />
                    <span>Get Help Online</span>
                  </a>
                </div>
              </div>

              {/* Hero Image/Illustration */}
              <div className="hidden lg:block">
                <div className="bg-white/10 backdrop-blur-lg border-2 border-white/30 rounded-3xl p-8 shadow-2xl">
                  <img 
                    src="public/assets/images/162111a8-97b7-4fd6-a950-171d24f8aaac-1764521073501.pngChatGPT_Image_Oct_28_2025_at_12_12_19_PM-1761678828931.png" 
                    alt="Compassionate bail support team helping families through difficult times"
                    className="w-full h-auto rounded-2xl shadow-xl"
                    onError={(e) => {
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Trust Indicators */}
        <section className="py-12 bg-white border-y-4 border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
              <div className="space-y-2">
                <div className="text-4xl font-black text-[#28A745]" style={{ fontFamily: 'Roboto Slab, serif' }}>
                  2min
                </div>
                <div className="text-base font-semibold text-gray-700" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                  Average Response Time
                </div>
              </div>
              <div className="space-y-2">
                <div className="text-4xl font-black text-[#007BFF]" style={{ fontFamily: 'Roboto Slab, serif' }}>
                  24/7
                </div>
                <div className="text-base font-semibold text-gray-700" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                  Always Available
                </div>
              </div>
              <div className="space-y-2">
                <div className="text-4xl font-black text-purple-600" style={{ fontFamily: 'Roboto Slab, serif' }}>
                  15+
                </div>
                <div className="text-base font-semibold text-gray-700" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                  Years Experience
                </div>
              </div>
              <div className="space-y-2">
                <div className="text-4xl font-black text-amber-500" style={{ fontFamily: 'Roboto Slab, serif' }}>
                  2009
                </div>
                <div className="text-base font-semibold text-gray-700" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                  In Bail Bonds Business Since
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Welcome Message */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center space-y-6">
            <h3 
              className="text-4xl font-black text-gray-900"
              style={{ fontFamily: 'Roboto Slab, serif' }}
            >
              We love our customers, so feel free to call us anytime
            </h3>
            <p 
              className="text-xl leading-relaxed text-gray-700"
              style={{ fontFamily: 'Open Sans, sans-serif' }}
            >
              We promise the best service around—our team is kind, understanding, and here to walk you through every step so you feel totally at ease. We're proud to offer rare perks like picking your loved one up from jail and transporting them home.
            </p>
          </div>
        </section>

        {/* Services Teaser */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h3 
                className="text-4xl font-black text-gray-900 mb-4"
                style={{ fontFamily: 'Roboto Slab, serif' }}
              >
                How We Help You
              </h3>
              <p 
                className="text-xl text-gray-600"
                style={{ fontFamily: 'Open Sans, sans-serif' }}
              >
                Comprehensive bail bond services designed with your needs in mind
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {/* Service Card 1 */}
              <div className="bg-gradient-to-br from-[#F8F9FA] to-blue-50 border-2 border-[#007BFF]/20 rounded-2xl p-8 hover:shadow-2xl transition-all transform hover:scale-105">
                <div className="bg-[#007BFF] w-16 h-16 rounded-2xl flex items-center justify-center mb-6 mx-auto">
                  <Car className="w-8 h-8 text-white" />
                </div>
                <h4 className="text-xl font-black text-gray-900 mb-3 text-center" style={{ fontFamily: 'Roboto Slab, serif' }}>
                  Jail Transportation
                </h4>
                <p className="text-base text-gray-700 text-center leading-relaxed" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                  We'll pick your loved one up from jail and bring them home safely—a rare service you won't find elsewhere.
                </p>
              </div>

              {/* Service Card 2 */}
              <div className="bg-gradient-to-br from-[#F8F9FA] to-green-50 border-2 border-[#28A745]/20 rounded-2xl p-8 hover:shadow-2xl transition-all transform hover:scale-105">
                <div className="bg-[#28A745] w-16 h-16 rounded-2xl flex items-center justify-center mb-6 mx-auto">
                  <CreditCard className="w-8 h-8 text-white" />
                </div>
                <h4 className="text-xl font-black text-gray-900 mb-3 text-center" style={{ fontFamily: 'Roboto Slab, serif' }}>
                  Flexible Payment Plans
                </h4>
                <p className="text-base text-gray-700 text-center leading-relaxed" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                  Affordable payment options tailored to your budget. We work with you to find a solution that fits.
                </p>
              </div>

              {/* Service Card 3 */}
              <div className="bg-gradient-to-br from-[#F8F9FA] to-purple-50 border-2 border-purple-300 rounded-2xl p-8 hover:shadow-2xl transition-all transform hover:scale-105">
                <div className="bg-purple-600 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 mx-auto">
                  <FileText className="w-8 h-8 text-white" />
                </div>
                <h4 className="text-xl font-black text-gray-900 mb-3 text-center" style={{ fontFamily: 'Roboto Slab, serif' }}>
                  Bail Process Guidance
                </h4>
                <p className="text-base text-gray-700 text-center leading-relaxed" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                  Wondering about the process? We'll explain it all simply and walk you through each step with patience.
                </p>
              </div>

              {/* Service Card 4 */}
              <div className="bg-gradient-to-br from-[#F8F9FA] to-amber-50 border-2 border-amber-300 rounded-2xl p-8 hover:shadow-2xl transition-all transform hover:scale-105">
                <div className="bg-amber-500 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 mx-auto">
                  <Shield className="w-8 h-8 text-white" />
                </div>
                <h4 className="text-xl font-black text-gray-900 mb-3 text-center" style={{ fontFamily: 'Roboto Slab, serif' }}>
                  Licensed & Trusted
                </h4>
                <p className="text-base text-gray-700 text-center leading-relaxed" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                  Fully licensed California bail agents with over 15 years of experience serving families with care.
                </p>
              </div>
            </div>

            <div className="text-center mt-12">
              <Link 
                to="/payment-plans-page"
                className="inline-flex items-center space-x-2 bg-[#007BFF] text-white px-8 py-4 rounded-xl font-bold text-lg hover:bg-blue-700 transition-all transform hover:scale-105 shadow-lg"
              >
                <span>Learn More About Our Services</span>
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-blue-50 to-[#F8F9FA]">
          <div className="max-w-7xl mx-auto">
            <div className="text-center mb-16">
              <h3 
                className="text-4xl font-black text-gray-900 mb-4"
                style={{ fontFamily: 'Roboto Slab, serif' }}
              >
                What Our Clients Say
              </h3>
              <p 
                className="text-xl text-gray-600"
                style={{ fontFamily: 'Open Sans, sans-serif' }}
              >
                Real stories from families we've helped
              </p>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {/* Testimonial 1 */}
              <div className="bg-white border-2 border-[#007BFF]/20 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all">
                <div className="flex items-center mb-4">
                  {[1, 2, 3, 4, 5]?.map((star) => (
                    <Star key={star} className="w-5 h-5 text-amber-400 fill-current" />
                  ))}
                </div>
                <p className="text-base text-gray-700 mb-6 leading-relaxed italic" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                  "Sailors made a scary process easy—thanks! The team was so understanding and patient with me during a really difficult time."
                </p>
                <div className="flex items-center">
                  <div className="bg-[#007BFF] w-12 h-12 rounded-full flex items-center justify-center text-white font-bold">
                    JM
                  </div>
                  <div className="ml-4">
                    <p className="font-bold text-gray-900">Jessica M.</p>
                    <p className="text-sm text-gray-600">Sacramento, CA</p>
                  </div>
                </div>
              </div>

              {/* Testimonial 2 */}
              <div className="bg-white border-2 border-[#28A745]/20 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all">
                <div className="flex items-center mb-4">
                  {[1, 2, 3, 4, 5]?.map((star) => (
                    <Star key={star} className="w-5 h-5 text-amber-400 fill-current" />
                  ))}
                </div>
                <p className="text-base text-gray-700 mb-6 leading-relaxed italic" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                  "The transportation service was a lifesaver. They picked my son up and brought him straight home. Amazing service!"
                </p>
                <div className="flex items-center">
                  <div className="bg-[#28A745] w-12 h-12 rounded-full flex items-center justify-center text-white font-bold">
                    RH
                  </div>
                  <div className="ml-4">
                    <p className="font-bold text-gray-900">Robert H.</p>
                    <p className="text-sm text-gray-600">Stockton, CA</p>
                  </div>
                </div>
              </div>

              {/* Testimonial 3 */}
              <div className="bg-white border-2 border-purple-300 rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all">
                <div className="flex items-center mb-4">
                  {[1, 2, 3, 4, 5]?.map((star) => (
                    <Star key={star} className="w-5 h-5 text-amber-400 fill-current" />
                  ))}
                </div>
                <p className="text-base text-gray-700 mb-6 leading-relaxed italic" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                  "Professional, fast, and empathetic. They explained everything clearly and worked with our budget. Highly recommend!"
                </p>
                <div className="flex items-center">
                  <div className="bg-purple-600 w-12 h-12 rounded-full flex items-center justify-center text-white font-bold">
                    ML
                  </div>
                  <div className="ml-4">
                    <p className="font-bold text-gray-900">Maria L.</p>
                    <p className="text-sm text-gray-600">Modesto, CA</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Emergency Form Section */}
        <section id="emergency-form" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto">
            <div className="text-center mb-12">
              <h3 
                className="text-4xl font-black text-gray-900 mb-4"
                style={{ fontFamily: 'Roboto Slab, serif' }}
              >
                Need Help Now?
              </h3>
              <p 
                className="text-xl text-gray-600"
                style={{ fontFamily: 'Open Sans, sans-serif' }}
              >
                Fill out this form and a licensed agent will contact you within 2 minutes
              </p>
            </div>

            <EmergencyBailAssistanceForm />
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-gray-900 text-white py-16 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="grid md:grid-cols-4 gap-12 mb-12">
              {/* Company Info */}
              <div className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Anchor className="w-10 h-10 text-[#007BFF]" />
                  <h4 className="text-2xl font-black" style={{ fontFamily: 'Roboto Slab, serif' }}>
                    Sailors Bail Bonds
                  </h4>
                </div>
                <p className="text-gray-400 leading-relaxed" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                  In bail bonds business since 2009, serving all California counties with compassionate, professional service.
                </p>
              </div>

              {/* Contact */}
              <div className="space-y-4">
                <h5 className="text-xl font-bold mb-4" style={{ fontFamily: 'Roboto Slab, serif' }}>
                  Contact Us
                </h5>
                <div className="space-y-3">
                  <a 
                    href={`tel:${EMERGENCY_PHONE?.replace(/\D/g, '')}`}
                    className="flex items-center space-x-2 text-gray-300 hover:text-[#28A745] transition-colors"
                  >
                    <Phone className="w-5 h-5" />
                    <span>{EMERGENCY_PHONE}</span>
                  </a>
                  <div className="flex items-start space-x-2 text-gray-300">
                    <MapPin className="w-5 h-5 mt-1 flex-shrink-0" />
                    <span>{OFFICE_ADDRESS}</span>
                  </div>
                  <div className="flex items-center space-x-2 text-gray-300">
                    <Clock className="w-5 h-5" />
                    <span>24/7 - Always Available</span>
                  </div>
                </div>
              </div>

              {/* Quick Links */}
              <div className="space-y-4">
                <h5 className="text-xl font-bold mb-4" style={{ fontFamily: 'Roboto Slab, serif' }}>
                  Quick Links
                </h5>
                <nav className="flex flex-col space-y-2" aria-label="Footer navigation">
                  <Link to="/about-us" className="text-gray-300 hover:text-[#007BFF] transition-colors">
                    About Us
                  </Link>
                  <Link to="/payment-plans-page" className="text-gray-300 hover:text-[#007BFF] transition-colors">
                    Services
                  </Link>
                  <Link to="/inmate-search-widget" className="text-gray-300 hover:text-[#007BFF] transition-colors">
                    Inmate Search
                  </Link>
                  <Link to="/5-step-bail-guide-with-faq" className="text-gray-300 hover:text-[#007BFF] transition-colors">
                    Bail Guide & FAQ
                  </Link>
                </nav>
              </div>

              {/* Trust Badges */}
              <div className="space-y-4">
                <h5 className="text-xl font-bold mb-4" style={{ fontFamily: 'Roboto Slab, serif' }}>
                  Why Trust Us
                </h5>
                <div className="space-y-3">
                  <div className="flex items-center space-x-2 text-gray-300">
                    <Award className="w-5 h-5 text-[#007BFF]" />
                    <span>Licensed & Insured</span>
                  </div>
                  <div className="flex items-center space-x-2 text-gray-300">
                    <Heart className="w-5 h-5 text-[#28A745]" />
                    <span>Family Owned</span>
                  </div>
                  <div className="flex items-center space-x-2 text-gray-300">
                    <Zap className="w-5 h-5 text-amber-400" />
                    <span>Fast Response</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Copyright */}
            <div className="border-t border-gray-800 pt-8 text-center">
              <p className="text-gray-400" style={{ fontFamily: 'Open Sans, sans-serif' }}>
                © {new Date()?.getFullYear()} Sailors Bail Bonds. All rights reserved.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default Homepage;